/**
 * What one seat is allowed to see. The server sends only this; the in-browser
 * driver produces the same shape so one GameScreen serves all three modes.
 */

import type { Card } from './cards.js';
import { type GameState, type Phase, type Seat, type ShowStep, legalPlays, other } from './engine.js';
import type { Score } from './scoring.js';

export interface PegView {
  stack: Card[];
  count: number;
  turn: Seat;
  go: [boolean, boolean];
  played: [Card[], Card[]];
  /** Cards in YOUR hand that fit under 31 right now (empty if not your turn). */
  legal: Card[];
  /** You hold cards, none fits, and it is your turn. */
  mustGo: boolean;
}

export interface ShowView {
  step: ShowStep;
  /** Whose cards are being counted. */
  seat: Seat;
  cards: Card[];
  score: Score;
  /** Revealed progressively: pone's kept four, then dealer's, then crib. */
  pone: Card[];
  dealer: Card[] | null;
  crib: Card[] | null;
}

export interface View {
  seq: number;
  phase: Phase;
  you: Seat;
  dealer: Seat | null;
  scores: [number, number];
  handNo: number;
  hand: Card[];
  oppCount: number;
  cribCount: number;
  starter: Card | null;
  cutCards: [Card | null, Card | null];
  submitted: [boolean, boolean];
  acked: [boolean, boolean];
  peg: PegView | null;
  show: ShowView | null;
  winner: Seat | null;
  skunk: 0 | 1 | 2;
  /** Hand-end only: both players' kept cards and the crib, for the summary. */
  lastHand: { kept: [Card[], Card[]]; crib: Card[]; starter: Card } | null;
}

export function viewFor(s: GameState, you: Seat): View {
  const opp = other(you);
  let peg: PegView | null = null;
  if (s.peg) {
    const mine = s.peg.turn === you ? legalPlays(s.hands[you], s.peg.count) : [];
    peg = {
      stack: [...s.peg.stack],
      count: s.peg.count,
      turn: s.peg.turn,
      go: [s.peg.go[0], s.peg.go[1]],
      played: [[...s.peg.played[0]], [...s.peg.played[1]]],
      legal: mine,
      mustGo: s.peg.turn === you && s.hands[you].length > 0 && mine.length === 0,
    };
  }
  let show: ShowView | null = null;
  if (s.show && s.dealer !== null) {
    const d = s.dealer;
    const p = other(d);
    const seat = s.show.step === 'pone' ? p : d;
    const cards = s.show.step === 'crib' ? s.crib : s.kept[seat];
    show = {
      step: s.show.step,
      seat,
      cards: [...cards],
      score: s.show.score,
      pone: [...s.kept[p]],
      dealer: s.show.step === 'pone' ? null : [...s.kept[d]],
      crib: s.show.step === 'crib' ? [...s.crib] : null,
    };
  }
  const lastHand =
    (s.phase === 'hand-end' || s.phase === 'game-over') && s.starter !== null && s.kept[0].length === 4
      ? { kept: [[...s.kept[0]], [...s.kept[1]]] as [Card[], Card[]], crib: [...s.crib], starter: s.starter }
      : null;
  return {
    seq: s.seq,
    phase: s.phase,
    you,
    dealer: s.dealer,
    scores: [s.scores[0], s.scores[1]],
    handNo: s.handNo,
    hand: [...s.hands[you]],
    oppCount: s.hands[opp].length,
    cribCount: s.crib.length,
    starter: s.starter,
    cutCards: [s.cutCards[0], s.cutCards[1]],
    submitted: [s.submitted[0], s.submitted[1]],
    acked: [s.acked[0], s.acked[1]],
    peg,
    show,
    winner: s.winner,
    skunk: s.skunk,
    lastHand,
  };
}
